/**
 * @fileoverview AI-guided interview modal for generating inspection template metadata.
 * @module ui-admin/components/ai/SquirrelInterviewModal
 *
 * Responsibilities:
 * - Collect template requirements through a conversational interview.
 * - Accept document-derived field suggestions and emit completed metadata.
 * Dependencies: React, lucide-react, ThemeContext; message and upload callbacks from the host.
 * Architecture: Presentation component with in-memory conversation state and injected operations.
 * Notes: Conversation history is reset by Start Over and is not persisted by this component.
 */
import { useState, useRef, useEffect } from 'react';
import { X, Send, RotateCcw, Sparkles, Loader2, Paperclip } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * @contract SquirrelChatMessage
 * Purpose: Represents one role-tagged message exchanged during the interview.
 * Fields: role, content.
 * Rules: role identifies system, user, or assistant content.
 * Sync: Passed to the injected message sender; conversation history is component-local.
 */
export interface SquirrelChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

/**
 * @contract ExtractedMetadata
 * Purpose: Carries the template metadata produced by a completed interview.
 * Fields: Template identity/type, suggested fields, regulatory/photo/signature requirements, and capability flags.
 * Rules: templateType and requirement fields use the declared finite option sets.
 * Sync: Returned to the host through onComplete; persistence is owned by the host.
 */
export interface ExtractedMetadata {
  templateName: string;
  description: string;
  templateType: 'wildland' | 'fireCode' | 'hydrant' | 'complex' | 'communityRisk' | 'custom';
  suggestedFields: Array<{
    label: string;
    type: string;
    required: boolean;
    dataBinding?: string;
  }>;
  regulatoryReferences: string[];
  photoRequirements: 'required' | 'optional' | 'none';
  signatureRequirements: string[];
  offlineCapable: boolean;
  severityTracking: boolean;
  reinspectionTriggers?: string[];
  polymorphicType?: string;
  visibility?: 'tenant' | 'organization' | 'system';
}

/**
 * @contract SquirrelSendResult
 * Purpose: Describes a response from the injected conversational service.
 * Fields: response, isComplete, metadata (optional).
 * Rules: metadata may be supplied when the interview is complete.
 * Sync: Produced by the host-provided message sender.
 */
export interface SquirrelSendResult {
  response: string;
  isComplete: boolean;
  metadata?: ExtractedMetadata;
}

/**
 * @contract SquirrelMessageSender
 * Purpose: Defines the async boundary used to send interview messages.
 * Fields: conversation history, new user message, interview context.
 * Rules: Resolves with response text and optional completed metadata.
 * Sync: Host-provided operation; this component does not own transport or persistence.
 */
export type SquirrelMessageSender = (
  conversationHistory: SquirrelChatMessage[],
  userMessage: string,
  context: SquirrelInterviewContext,
) => Promise<SquirrelSendResult>;

/**
 * @contract SquirrelInterviewContext
 * Purpose: Provides purpose and optional template context for the interview.
 * Fields: purpose, templateType (optional), entityCategory (optional).
 * Rules: purpose distinguishes department and library creation flows.
 * Sync: Context is passed to the injected message sender.
 */
export interface SquirrelInterviewContext {
  purpose: 'department' | 'library';
  templateType?: 'inspection' | 'import' | 'report' | 'dashboard' | 'document' | 'email';
  entityCategory?: string;
}

/**
 * @contract SquirrelInterviewModalProps
 * Purpose: Configures modal visibility, interview operations, and generated-metadata handling.
 * Fields: isOpen, onClose, onComplete, messageSender, context, avatarUrl, onDocumentUpload.
 * Rules: Document parsing is optional; onComplete receives extracted metadata.
 * Sync: Host supplies service callbacks and owns generated metadata persistence.
 */
export interface SquirrelInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (metadata: ExtractedMetadata) => void;
  messageSender: SquirrelMessageSender;
  context?: SquirrelInterviewContext;
  avatarUrl?: string;
  onDocumentUpload?: (file: File) => Promise<Array<{ label: string; type: string; required: boolean }>>;
}

interface DisplayMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component SquirrelInterviewModal
 * Purpose: Guide a user through collecting inspection-template requirements.
 * Props: Open/close state, completion callback, message sender, context, avatar, and upload callback.
 * Behavior: Renders a themed chat, progress feedback, optional document upload, and completion actions.
 * Data Source: hybrid (local conversation state with host-provided message and upload operations).
 * Constraints: The host owns AI transport, document parsing, and generated metadata handling.
 * Performance: Keeps one in-memory conversation and scrolls the message list to the latest response.
 */
export default function SquirrelInterviewModal({
  isOpen,
  onClose,
  onComplete,
  messageSender,
  context = { purpose: 'department' },
  avatarUrl,
  onDocumentUpload,
}: SquirrelInterviewModalProps) {
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [extractedMetadata, setExtractedMetadata] = useState<ExtractedMetadata | null>(null);
  const [conversationHistory, setConversationHistory] = useState<SquirrelChatMessage[]>([]);
  const [collectedCount, setCollectedCount] = useState(0);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { theme } = useTheme();
  const isLibrary = context.purpose === 'library';
  const accentCls = isLibrary
    ? 'from-purple-500 to-purple-700'
    : 'from-orange-400 to-orange-600';
  const ringCls = isLibrary ? 'focus:ring-purple-500' : 'focus:ring-orange-500';
  const btnCls = isLibrary
    ? 'bg-linear-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800'
    : 'bg-linear-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700';

  const subtitle = isLibrary
    ? 'System library template — AI-guided setup'
    : 'AI-guided template creation';

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const opener = isLibrary
        ? "Hi! I'm Squirrel. Let's build a system library template together. Library templates can be adopted by any department, so I'll ask a few extra questions about scope and polymorphic structure.\n\nTo get started — what type of inspection does this template cover, and who will use it?"
        : "Hi! I'm Squirrel, your inspection template assistant. Let's build a template together.\n\nWhat type of inspection does this template cover? (e.g., \"annual fire code\", \"defensible space\", \"commercial kitchen\", or describe what you're inspecting)";
      setMessages([{
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: opener,
        timestamp: new Date(),
      }]);
    }
  }, [isOpen, messages.length, isLibrary]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const text = inputValue;
    setInputValue('');

    const userDisplay: DisplayMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userDisplay]);
    setIsLoading(true);

    const updatedHistory: SquirrelChatMessage[] = [
      ...conversationHistory,
      { role: 'user', content: text },
    ];

    try {
      const result = await messageSender(conversationHistory, text, context);

      const assistantDisplay: DisplayMessage = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: result.response,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantDisplay]);
      setConversationHistory([
        ...updatedHistory,
        { role: 'assistant', content: result.response },
      ]);

      const newCount = Math.min(updatedHistory.filter(m => m.role === 'user').length, 10);
      setCollectedCount(newCount);

      if (result.isComplete && result.metadata) {
        setIsComplete(true);
        setExtractedMetadata(result.metadata);
      }
    } catch {
      setMessages(prev => [...prev, {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: "I'm having trouble reaching my AI service right now. Please try again in a moment.",
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleStartOver = () => {
    setMessages([]);
    setIsComplete(false);
    setExtractedMetadata(null);
    setConversationHistory([]);
    setCollectedCount(0);
  };

  const handleGenerate = () => {
    if (extractedMetadata) {
      onComplete(extractedMetadata);
      onClose();
    }
  };

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onDocumentUpload) return;
    e.target.value = '';

    setIsUploadingDoc(true);
    const uploadingMsg: DisplayMessage = {
      id: `msg-upload-${Date.now()}`,
      role: 'assistant',
      content: `Reading your document "${file.name}"…`,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, uploadingMsg]);

    try {
      const fields = await onDocumentUpload(file);

      const fieldList = fields
        .slice(0, 30)
        .map(f => `- ${f.label} (${f.type}${f.required ? ', required' : ''})`)
        .join('\n');

      const contextMessage = `I've uploaded "${file.name}". Here are the fields I found in it:\n\n${fieldList}\n\nPlease use this as the foundation for the template. Ask me about anything it doesn't make clear.`;

      const contextDisplay: DisplayMessage = {
        id: `msg-doc-${Date.now()}`,
        role: 'user',
        content: contextMessage,
        timestamp: new Date(),
      };

      setMessages(prev => [
        ...prev.filter(m => m.id !== uploadingMsg.id),
        contextDisplay,
      ]);

      const updatedHistory: SquirrelChatMessage[] = [
        ...conversationHistory,
        { role: 'user', content: contextMessage },
      ];

      setIsLoading(true);
      const result = await messageSender(conversationHistory, contextMessage, context);

      const assistantDisplay: DisplayMessage = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: result.response,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantDisplay]);
      setConversationHistory([
        ...updatedHistory,
        { role: 'assistant', content: result.response },
      ]);
      setCollectedCount(prev => Math.min(prev + 2, 10));

      if (result.isComplete && result.metadata) {
        setIsComplete(true);
        setExtractedMetadata(result.metadata);
      }
    } catch (err: any) {
      setMessages(prev => [
        ...prev.filter(m => m.id !== uploadingMsg.id),
        {
          id: `msg-err-${Date.now()}`,
          role: 'assistant',
          content: `I couldn't read that document. ${err?.message ?? 'Please try again or describe the form manually.'}`,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsUploadingDoc(false);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const progressPct = Math.min((collectedCount / 8) * 100, 100);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="rounded-xl shadow-2xl w-full max-w-3xl h-[82vh] flex flex-col" style={{ background: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: `1px solid ${theme.cardBorder}` }}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 bg-linear-to-br ${accentCls} rounded-full flex items-center justify-center overflow-hidden`}>
              {avatarUrl
                ? <img src={avatarUrl} alt="Squirrel" className="w-10 h-10 object-cover" />
                : <Sparkles className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h2 className="text-base font-semibold" style={{ color: theme.textPrimary }}>Squirrel Template Builder</h2>
              <p className="text-xs" style={{ color: theme.textSecondary }}>{subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="transition-colors p-1" style={{ color: theme.textMuted }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress — only shown once conversation has started */}
        {collectedCount > 0 && !isComplete && (
          <div className="px-6 py-2.5 shrink-0" style={{ borderBottom: `1px solid ${theme.cardBorder}`, background: theme.toolbarBg }}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs" style={{ color: theme.textSecondary }}>Gathering requirements</span>
              <span className="text-xs font-medium" style={{ color: theme.textPrimary }}>{Math.round(progressPct)}%</span>
            </div>
            <div className="w-full rounded-full h-1.5" style={{ background: theme.inputBorder }}>
              <div
                className={`bg-linear-to-r ${accentCls} h-1.5 rounded-full transition-all duration-500`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-0">
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className="max-w-[80%] rounded-xl px-4 py-3 text-sm"
                style={msg.role === 'user'
                  ? { background: theme.accent, color: '#ffffff' }
                  : { background: theme.toolbarBg, color: theme.textPrimary, border: `1px solid ${theme.toolbarBorder}` }
                }
              >
                {msg.role === 'assistant' && (
                  <div className="flex items-center gap-1.5 mb-1">
                    {avatarUrl
                      ? <img src={avatarUrl} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
                      : <Sparkles className="w-3.5 h-3.5" style={{ color: theme.accentText }} />}
                    <span className="text-[11px] font-semibold" style={{ color: theme.textSecondary }}>Squirrel</span>
                  </div>
                )}
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="rounded-xl px-4 py-3" style={{ background: theme.toolbarBg, border: `1px solid ${theme.toolbarBorder}` }}>
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: theme.accentText }} />
                  <span className="text-sm" style={{ color: theme.textSecondary }}>Squirrel is thinking…</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input / Complete area */}
        <div className="px-6 py-4 shrink-0" style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
          {isComplete ? (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-green-900 mb-0.5">Ready to build!</h3>
                    <p className="text-xs text-green-700">
                      I've collected everything needed. Click "Generate Template" to create it and open the editor.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleStartOver}
                  className="flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                  style={{ border: `1px solid ${theme.cardBorder}`, color: theme.textPrimary, background: 'transparent' }}
                  onMouseEnter={e => (e.currentTarget.style.background = theme.toolbarBg)}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <RotateCcw className="w-4 h-4" /> Start Over
                </button>
                <button
                  onClick={handleGenerate}
                  className={`flex-1 px-4 py-2 ${btnCls} text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-medium`}
                >
                  <Sparkles className="w-4 h-4" /> Generate Template
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              {onDocumentUpload && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.txt"
                    className="hidden"
                    onChange={handleDocumentUpload}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading || isUploadingDoc}
                    title="Upload an existing form document to base the template on"
                    className="px-3 py-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ border: `1px solid ${theme.inputBorder}`, color: theme.textMuted, background: 'transparent' }}
                  >
                    {isUploadingDoc
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Paperclip className="w-4 h-4" />}
                  </button>
                </>
              )}
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your response…"
                disabled={isLoading || isUploadingDoc}
                className="flex-1 px-4 py-2 rounded-lg text-sm focus:outline-hidden focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ border: `1px solid ${theme.inputBorder}`, background: theme.inputBg, color: theme.textPrimary }}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading || isUploadingDoc}
                className={`px-5 py-2 ${btnCls} text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm`}
              >
                <Send className="w-4 h-4" /> Send
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
